import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { computed, type Ref } from 'vue'

import type { MergedColumnData, ResponsiveBreakpoint, TableDataColumn } from './types'

const breakpointOrder: ResponsiveBreakpoint[] = ['sm', 'md', 'lg', 'xl']

export function useResponsiveColumns(columns: Ref<TableDataColumn[]>) {
  const breakpoints = useBreakpoints(breakpointsTailwind)

  const isBelow = computed(() => ({
    sm: breakpoints.smaller('sm').value,
    md: breakpoints.smaller('md').value,
    lg: breakpoints.smaller('lg').value,
    xl: breakpoints.smaller('xl').value
  }))

  const hiddenColumnKeys = computed(() => {
    const hidden = new Set<string>()
    for (const column of columns.value) {
      if (column.responsive?.hideBelow) {
        const breakpoint = column.responsive.hideBelow
        const breakpointIndex = breakpointOrder.indexOf(breakpoint)
        if (breakpointIndex !== -1 && isBelow.value[breakpoint]) {
          hidden.add(column.key)
        }
      }
    }
    return hidden
  })

  const visibleColumns = computed(() =>
    columns.value.filter(col => !hiddenColumnKeys.value.has(col.key))
  )

  const mergeMap = computed(() => {
    const map = new Map<string, TableDataColumn[]>()
    for (const column of columns.value) {
      if (
        column.responsive?.mergeInto &&
        column.responsive.hideBelow &&
        hiddenColumnKeys.value.has(column.key)
      ) {
        const targetKey = column.responsive.mergeInto
        if (!map.has(targetKey)) {
          map.set(targetKey, [])
        }
        map.get(targetKey)!.push(column)
      }
    }
    return map
  })

  function getMergedDataForRow(
    targetColumnKey: string,
    row: Record<string, any>
  ): MergedColumnData[] {
    const mergedColumns = mergeMap.value.get(targetColumnKey) || []
    return mergedColumns.map(col => ({
      key: col.key,
      value: row[col.key],
      header: col.header,
      mergeAs: col.responsive?.mergeAs || 'secondLine',
      mergeLabel: col.responsive?.mergeLabel
    }))
  }

  function getMergedHeadersForColumn(targetColumnKey: string): string[] {
    const mergedColumns = mergeMap.value.get(targetColumnKey) || []
    return mergedColumns.filter(col => col.responsive?.mergeHeader).map(col => col.header)
  }

  return {
    visibleColumns,
    hiddenColumnKeys,
    getMergedDataForRow,
    getMergedHeadersForColumn
  }
}
